import { useContext } from 'react';
import type { AxiosError, AxiosResponse } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { ChatActionType, type BrandInsightsInstanceContext, type ChatAction } from '@trinity/chat';
import { SegmentChatContext } from '@trinity/chat-components/components/BrandInsights/SegmentChat.Context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveApplicationIdentifierNullSafe } from '@trinity/chat-components/containers/UserApplications/useActiveApplicationIdentifier';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  ActiveChatSessionContext,
  ChatSessionFiltersContext,
  DisplayModeContext,
  SetPendingScrollContext,
} from '../context';

type MutationVars = {
  identifier: string;
  parentQueryIdentifier: string | null;
  query: string;
  action: ChatAction | null;
  context?: BrandInsightsInstanceContext.ItemWithMetadata;
  promptIdentifier?: string;
};

export const useSubmitQueryMutation = () => {
  const chat = useContext(ActiveChatSessionContext);
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const setPendingScroll = useContext(SetPendingScrollContext);
  const brandIdentifier = useActiveBrandIdentifierNullSafe();
  const applicationIdentifier = useActiveApplicationIdentifierNullSafe();
  const { segmentImmutableTag } = useContext(SegmentChatContext);
  const displayMode = useContext(DisplayModeContext);

  const apiClient = useChatApiClient();

  const filtersWithSegment = segmentImmutableTag
    ? {
        ...filters,
        tags: (filters.tags ?? []).some((t) => t.id === segmentImmutableTag.id)
          ? filters.tags
          : [
              ...(filters.tags ?? []),
              {
                id: segmentImmutableTag.id,
                name: segmentImmutableTag.name,
                metadata: { canRemove: false, segmentIdentifier: segmentImmutableTag.segmentIdentifier },
              },
            ],
      }
    : filters;

  return useMutation({
    mutationKey: ['chat:message:submit'],
    mutationFn: (vars: MutationVars) => {
      return apiClient.chat.submitChatQuery({
        action: vars.action,
        chatIdentifier: chat.identifier,
        identifier: vars.identifier,
        parentQueryIdentifier: vars.parentQueryIdentifier,
        query: vars.query,
        context: vars.context || chat.context,
        filters: filtersWithSegment,
        brandIdentifier,
        applicationIdentifier,
        promptIdentifier: vars.promptIdentifier,
        displayMode,
      });
    },
    onMutate: ({ action, identifier, parentQueryIdentifier, query }) => {
      dispatch({
        type: 'query-submitted',
        payload: {
          attachments: [],
          chatIdentifier: chat.identifier,
          identifier,
          parentQueryIdentifier,
          value: query,
          hideUserMessage: !!action && action.type !== ChatActionType.ChartQuantAnalyses,
          brandIdentifier,
        },
      });
      if (setPendingScroll) {
        setPendingScroll();
      }
    },
    onError: (error: AxiosError<AxiosResponse>, vars) => {
      if (error.response?.status === 504 || error.status === 504) {
        dispatch({
          type: 'query-errored',
          payload: {
            langfuseTraceUrl: null,
            queryIdentifier: vars.identifier,
            traceId: null,
          },
        });
        if (setPendingScroll) {
          setPendingScroll();
        }
        return;
      }

      if (error.response?.status === 429 || error.status === 429) {
        dispatch({
          type: 'query-errored',
          payload: {
            langfuseTraceUrl: null,
            queryIdentifier: vars.identifier,
            traceId: null,
          },
        });
        if (setPendingScroll) {
          setPendingScroll();
        }
        return;
      }

      const traceId = error.response?.headers['x-sentry-trace-id'] as string;
      dispatch({
        type: 'query-errored',
        payload: {
          langfuseTraceUrl: null,
          queryIdentifier: vars.identifier,
          traceId,
        },
      });
      if (setPendingScroll) {
        setPendingScroll();
      }
    },
  });
};
